import { ResultOf, VariablesOf } from '@graphql-typed-document-node/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { IS_LOYALTY_ENABLED } from 'constants/loyalty.constants';
import { useTicketsClient } from 'services/api';
import {
  LoyaltyEffectFragment,
  SimulateLoyaltyCartItemInput,
  SimulateLoyaltyCartUpdateDocument,
} from 'types/api/tickets.types';

import { loyaltyEffectsQuery } from './queries';
import { AppSyncTicketsGraphQLError } from './types';

// Internal hook that handles the mutation logic
const useSimulateLoyaltyCartMutate = () => {
  const ticketsClient = useTicketsClient();
  const queryClient = useQueryClient();

  const { isPending, isPaused, mutate } = useMutation<
    ResultOf<typeof SimulateLoyaltyCartUpdateDocument>,
    AppSyncTicketsGraphQLError,
    VariablesOf<typeof SimulateLoyaltyCartUpdateDocument>
  >({
    mutationFn: (input) =>
      ticketsClient.request(SimulateLoyaltyCartUpdateDocument, input),
    retry: false,
    onError: () => {
      return queryClient.removeQueries({
        queryKey: loyaltyEffectsQuery.queryKey,
      });
    },
    onSuccess: (data) => {
      return queryClient.setQueryData(
        loyaltyEffectsQuery.queryKey,
        data.simulateLoyaltyCartUpdate,
      );
    },
  });

  return {
    simulateLoyaltyCartUpdate: mutate,
    isLoading: isPending && !isPaused,
  };
};

// Public hook that components will use
export const useSimulateLoyaltyCartUpdate = (
  cartId: string | undefined,
  cartItems: Array<SimulateLoyaltyCartItemInput>,
) => {
  const queryClient = useQueryClient();
  const hasTriggeredSimulation = useRef(false);

  // Use the internal mutation hook
  const { simulateLoyaltyCartUpdate, isLoading } =
    useSimulateLoyaltyCartMutate();

  // Get effects from query cache
  const effects = queryClient.getQueryData<Array<LoyaltyEffectFragment>>(
    loyaltyEffectsQuery.queryKey,
  );

  // Run simulation when cart changes
  useDeepCompareEffect(() => {
    const shouldSimulate =
      IS_LOYALTY_ENABLED &&
      !!cartId &&
      !!cartItems?.length &&
      !hasTriggeredSimulation.current;

    if (shouldSimulate) {
      hasTriggeredSimulation.current = true;
      simulateLoyaltyCartUpdate(
        { cartId, cartItems },
        {
          onError: () => {
            hasTriggeredSimulation.current = false;
          },
        },
      );
    }
  }, [cartId, cartItems]);

  // Reset simulation flag when cart changes
  useDeepCompareEffect(() => {
    hasTriggeredSimulation.current = false;
  }, [cartId, cartItems]);

  return {
    discountValue: effects?.[0]?.value ?? 0,
    isLoading,
  };
};
